import { Subject, timer } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

export type SasTokenConfig = {
  token: string
  expiresAt: Date
  containerUrl?: string
}

export type SasTokenEntry = {
  token: string
  expiresAt: Date
  containerUrl?: string
  refreshTimer$: Subject<void>
}

export type SasTokenResponse = {
  [datasource: string]: {
    token: string
    expiresAt: Date
    containerUrl: string
  } // Maps datasource to full URL with embedded SAS token
}

/**
 * Utility class to manage multiple SAS tokens with auto-refresh capability.
 * Can handle tokens for different containers or storage accounts.
 */
export class SasTokenManager {
  private tokens = new Map<string, SasTokenEntry>()
  private destroy$ = new Subject<void>()
  private refreshCallback?: (containerId: string) => Promise<void>

  constructor(refreshCallback?: (containerId: string) => Promise<void>) {
    this.refreshCallback = refreshCallback
  }

  /**
   * Set or update a SAS token for a specific container/identifier.
   * @param containerId Unique identifier (e.g., container name or custom key)
   * @param config Token configuration with expiry
   */
  setToken(containerId: string, config: SasTokenConfig): void {
    // Cancel existing refresh timer if any
    const existing = this.tokens.get(containerId)
    if (existing) {
      existing.refreshTimer$.next()
      existing.refreshTimer$.complete()
    }

    const refreshTimer$ = new Subject<void>()
    this.tokens.set(containerId, {
      token: config.token,
      expiresAt: config.expiresAt,
      containerUrl: config.containerUrl,
      refreshTimer$,
    })

    this.scheduleTokenRefresh(containerId)
  }

  /**
   * Get the current SAS token for a container.
   * @param containerId Container identifier
   * @returns Token string or undefined if not found
   */
  getToken(containerId: string): string | undefined {
    return this.tokens.get(containerId)?.token
  }

  /**
   * Get the full token entry including expiry and container URL.
   * @param containerId Container identifier
   */
  getTokenEntry(containerId: string): SasTokenEntry | undefined {
    return this.tokens.get(containerId)
  }

  /**
   * Check if a token exists for the given container.
   */
  hasToken(containerId: string): boolean {
    return this.tokens.has(containerId)
  }

  /**
   * Check if a token is expired or about to expire (within 60 seconds).
   */
  isTokenExpired(containerId: string): boolean {
    const entry = this.tokens.get(containerId)
    if (!entry) return true

    const now = Date.now()
    const expiresIn = entry.expiresAt.getTime() - now
    return expiresIn <= 60_000 // Consider expired if less than 60 seconds left
  }

  /**
   * Remove a token from the manager.
   */
  removeToken(containerId: string): void {
    const entry = this.tokens.get(containerId)
    if (entry) {
      entry.refreshTimer$.next()
      entry.refreshTimer$.complete()
      this.tokens.delete(containerId)
    }
  }

  /**
   * Clear all tokens and cancel all refresh timers.
   */
  clearAll(): void {
    this.tokens.forEach((entry) => {
      entry.refreshTimer$.next()
      entry.refreshTimer$.complete()
    })
    this.tokens.clear()
  }

  /**
   * Clean up all resources.
   */
  destroy(): void {
    this.clearAll()
    this.destroy$.next()
    this.destroy$.complete()
  }

  /**
   * Schedule automatic token refresh 60 seconds before expiry.
   */
  private scheduleTokenRefresh(containerId: string): void {
    const entry = this.tokens.get(containerId)
    if (!entry) return

    const now = Date.now()
    const expiresIn = entry.expiresAt.getTime() - now
    // Refresh 60 seconds before expiry
    const refreshIn = Math.max(expiresIn - 60_000, 0)

    if (refreshIn <= 0) {
      // Already expired or about to expire, trigger refresh immediately
      if (this.refreshCallback) {
        this.refreshCallback(containerId).catch((err) => {
          console.error(`Failed to refresh token for ${containerId}:`, err)
        })
      }
      return
    }

    timer(refreshIn)
      .pipe(takeUntil(entry.refreshTimer$))
      .subscribe(() => {
        if (this.refreshCallback) {
          this.refreshCallback(containerId).catch((err) => {
            console.error(`Failed to refresh token for ${containerId}:`, err)
          })
        }
      })
  }
}
